import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { money, fmtViews } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const TABS = [["views", "Most views"], ["earned", "Most earned"]];
const PERIODS = [["week", "This week"], ["month", "This month"], ["all", "All time"]];

function Avatar({ initials, avatar, size = 34, bright }) {
  return (
    <div style={{ width: size, height: size, borderRadius: 999, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: avatar ? `url(${avatar}) center/cover` : "var(--raised)", border: "1px solid " + (bright ? "var(--accent-light)" : "var(--border-strong)"), color: "var(--sub)", fontSize: size * 0.38, fontWeight: 500 }}>
      {!avatar && (initials || "C")}
    </div>
  );
}

export default function Leaderboard({ full = false }) {
  const [tab, setTab] = useState("views");
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const { user } = useAuth();
  const nav = useNavigate();
  const perPage = 20;

  useEffect(() => {
    api.get("/public/leaderboard", { params: { tab, period, limit: full ? 100 : 10 } }).then((r) => { setData(r.data); setPage(0); }).catch(() => {});
  }, [tab, period, full]);

  const rows = data?.rows || [];
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  const paged = full ? rest.slice(page * perPage, page * perPage + perPage) : rest.slice(0, 7);
  const metric = (r) => (tab === "earned" ? money(r.earned) : fmtViews(r.views) + " views");
  const highlight = (col) => (tab === col ? { color: "var(--accent-light)", fontWeight: 500 } : {});

  return (
    <section aria-labelledby="lb-heading" data-testid="leaderboard-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <h2 id="lb-heading" style={{ fontSize: 18, margin: 0 }}>Top clippers</h2>
          <p style={{ fontSize: 13, color: "var(--muted)", margin: "4px 0 0" }}>Ranked by views generated and money earned on Clipup</p>
        </div>
        {!full && <button onClick={() => nav("/leaderboard")} className="focus-ring" data-testid="view-full-leaderboard" style={{ background: "none", border: "none", color: "var(--accent-light)", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>View full leaderboard <ChevronRight size={14} /></button>}
      </div>

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 18 }}>
          <SummaryCard label="Total views generated" value={fmtViews(data.summary.total_views)} />
          <SummaryCard label="Total paid to clippers" value={money(data.summary.total_paid)} />
          <SummaryCard label="Active clippers" value={data.summary.active_clippers} />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {TABS.map(([v, l]) => <button key={v} onClick={() => setTab(v)} data-testid={`lb-tab-${v}`} style={chip(tab === v)}>{l}</button>)}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {PERIODS.map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} data-testid={`lb-period-${v}`} style={chip(period === v)}>{l}</button>)}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="surface" style={{ borderRadius: 12, padding: 40, textAlign: "center", color: "var(--muted)" }}>No ranked clippers yet.</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 14 }} data-testid="lb-top3">
            {top3.map((r) => (
              <div key={r.user_id} className="surface" style={{ borderRadius: 12, padding: 16, border: "1px solid " + (r.rank === 1 ? "var(--accent-light)" : "var(--border)"), display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>#{r.rank}</span>
                  {r.rank === 1 && <Crown size={16} color="var(--accent-light)" aria-hidden="true" />}
                </div>
                <Avatar initials={r.initials} avatar={r.avatar} size={44} bright={r.rank === 1} />
                <div style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                <div style={{ fontSize: 22, fontWeight: 500 }}>{tab === "earned" ? money(r.earned) : fmtViews(r.views)}</div>
                <div style={{ fontSize: 12, color: "var(--muted)" }}>{tab === "earned" ? "earned" : "views"}</div>
              </div>
            ))}
          </div>

          {paged.length > 0 && (
            <div className="surface" style={{ borderRadius: 12, padding: 0, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ color: "var(--muted)", textAlign: "left" }}>
                  <th style={th}>#</th><th style={th}>Clipper</th><th style={th}>Clips</th>
                  <th style={{ ...th, ...highlight("views") }}>Views</th><th style={{ ...th, ...highlight("earned") }}>Earned</th>
                </tr></thead>
                <tbody>
                  {paged.map((r) => (
                    <tr key={r.user_id} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={td}>{r.rank}</td>
                      <td style={td}><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar initials={r.initials} avatar={r.avatar} /> {r.name}</div></td>
                      <td style={td}>{r.clips}</td>
                      <td style={{ ...td, ...highlight("views") }}>{fmtViews(r.views)}</td>
                      <td style={{ ...td, ...highlight("earned") }}>{money(r.earned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {full && rest.length > perPage && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
              <button disabled={page === 0} onClick={() => setPage(page - 1)} style={chip(false)}>Previous</button>
              <span style={{ color: "var(--muted)", fontSize: 13, alignSelf: "center" }}>Page {page + 1} of {Math.ceil(rest.length / perPage)}</span>
              <button disabled={(page + 1) * perPage >= rest.length} onClick={() => setPage(page + 1)} style={chip(false)}>Next</button>
            </div>
          )}

          {user?.role === "clipper" && data?.your_rank && (
            <div className="surface" style={{ borderRadius: 12, padding: 14, marginTop: 14, border: "1px solid var(--accent)", display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="lb-your-rank">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, color: "var(--accent-light)" }}>Your rank</span>
                <b>{data.your_rank.rank ? `#${data.your_rank.rank}` : "Unranked"}</b>
                <span style={{ color: "var(--sub)" }}>{data.your_rank.name}</span>
              </div>
              <div style={{ fontSize: 13, color: "var(--sub)" }}>{fmtViews(data.your_rank.views)} views · {money(data.your_rank.earned)}</div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SummaryCard({ label, value }) {
  return <div className="surface" style={{ borderRadius: 12, padding: 16 }}><div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div><div style={{ fontSize: 22, fontWeight: 500, marginTop: 4 }}>{value}</div></div>;
}
const chip = (a) => ({ padding: "7px 14px", borderRadius: 999, fontSize: 13, cursor: "pointer", border: "1px solid " + (a ? "var(--accent)" : "var(--border)"), background: a ? "rgba(42,79,214,.15)" : "transparent", color: a ? "var(--accent-light)" : "var(--sub)" });
const th = { padding: 12, fontWeight: 400 };
const td = { padding: 12 };
